import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { CalendarCheck, CalendarClock, ExternalLink, Sparkles } from 'lucide-react'
import { AppHeader } from '#/components/layout/app-header'
import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import { ApiError } from '#/lib/api'
import { useCalcomLink, useCoachingCheck, useCoachingList, useConfirmCoaching, useCreateCoaching, type CoachingSessionDto } from '#/lib/api-misc'
import { useHydratedSession } from '#/lib/auth-client'
import { fmtDateTime } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/coaching')({ component: Coaching })

function Coaching() {
  const navigate = useNavigate()
  const { data: session, isPending } = useHydratedSession()
  const signedIn = Boolean(session?.user)
  useEffect(() => {
    if (!isPending && !signedIn) navigate({ to: '/sign-in', search: { redirect: '/coaching' } })
  }, [isPending, signedIn, navigate])
  const check = useCoachingCheck(signedIn)
  const list = useCoachingList(signedIn)
  const calcom = useCalcomLink(signedIn)
  const create = useCreateCoaching()
  const confirm = useConfirmCoaching()

  const book = async () => {
    // Open the tab synchronously (popup blockers), then point it at the scheduler once the session exists.
    const link = calcom.data?.link ?? null
    const win = link ? window.open('', '_blank') : null
    try {
      const r = await create.mutateAsync()
      const target = r.session.calcomLink ?? link
      if (win && target) win.location.href = target
      else win?.close()
      toast.success(m.coaching_created())
      if (!target) toast.info(m.coaching_calcom_missing())
    } catch (e) {
      win?.close()
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }
  const markConfirmed = async (s: CoachingSessionDto) => {
    try {
      await confirm.mutateAsync({ sessionId: s.id })
      toast.success(m.coaching_confirmed_toast())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }

  const c = check.data
  const remainingLabel = !c ? m.common_loading() : c.remaining === -1 ? m.coaching_unlimited() : c.remaining === 1 ? m.coaching_remaining_one() : c.remaining > 0 ? m.coaching_remaining({ n: c.remaining }) : m.coaching_none()
  const statusLabel = (s: string) => (s === 'confirmed' ? m.coaching_status_confirmed() : s === 'cancelled' ? m.coaching_status_cancelled() : m.coaching_status_pending())
  const statusClass = (s: string) => (s === 'confirmed' ? 'bg-status-done/15 text-status-done' : s === 'cancelled' ? 'bg-status-failed/15 text-status-failed' : 'bg-status-progress/15 text-status-progress')

  return (
    <>
      <AppHeader />
      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-2xl space-y-6">
          <section className="space-y-4 rounded-2xl border bg-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <h1 className="flex items-center gap-2 text-2xl font-bold"><Sparkles className="size-5 text-primary" /> {m.coaching_title()}</h1>
                <p className="text-sm text-muted-foreground">{m.coaching_desc()}</p>
              </div>
              <Badge variant={c?.eligible ? 'default' : 'secondary'} className="shrink-0" data-testid="coaching-remaining">{remainingLabel}</Badge>
            </div>
            {c ? <p className="text-xs text-muted-foreground">{m.coaching_plan_line({ plan: c.planType, used: c.used, limit: c.limit === -1 ? '∞' : c.limit, credits: c.credits })}</p> : null}
            <div className="flex flex-wrap items-center gap-2">
              <Button aria-busy={create.isPending} onClick={book} disabled={!c?.eligible || create.isPending} data-testid="coaching-book">
                <CalendarClock className="size-4" /> {create.isPending ? m.coaching_booking() : m.coaching_book()}
              </Button>
              {c && !c.eligible ? <Button asChild variant="secondary"><Link to="/pricing" search={{ voucher: undefined }}>{m.coaching_upgrade()}</Link></Button> : null}
              {calcom.data?.configured && calcom.data.link ? (
                <Button asChild variant="ghost"><a href={calcom.data.link} target="_blank" rel="noreferrer"><ExternalLink className="size-4" /> {m.coaching_open_link()}</a></Button>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground">{calcom.data && !calcom.data.configured ? m.coaching_calcom_missing() : m.coaching_book_hint()}</p>
          </section>

          <section className="space-y-2 rounded-2xl border bg-card p-6">
            <h2 className="font-semibold">{m.coaching_how_title()}</h2>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li>{m.coaching_how_1()}</li>
              <li>{m.coaching_how_2()}</li>
              <li>{m.coaching_how_3()}</li>
            </ol>
          </section>

          <section className="space-y-3 rounded-2xl border bg-card p-6">
            <h2 className="font-semibold">{m.coaching_sessions_title()}</h2>
            {list.data?.sessions.length ? (
              <ul className="divide-y">
                {list.data.sessions.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm" data-testid="coaching-session">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(s.status)}`}>{statusLabel(s.status)}</span>
                        <span className="font-mono text-xs text-muted-foreground">{s.id.slice(0, 8)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {fmtDateTime(s.createdAt)}
                        {s.scheduledAt ? ` · ${m.coaching_scheduled_at()}: ${fmtDateTime(s.scheduledAt)}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {s.calcomLink && s.status === 'pending' ? <Button asChild size="sm" variant="ghost"><a href={s.calcomLink} target="_blank" rel="noreferrer"><ExternalLink className="size-4" /> {m.coaching_open_link()}</a></Button> : null}
                      {s.status === 'pending' ? <Button loading={confirm.isPending} size="sm" variant="secondary" onClick={() => markConfirmed(s)} disabled={confirm.isPending}><CalendarCheck className="size-4" /> {m.coaching_confirm()}</Button> : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{list.isPending ? m.common_loading() : m.coaching_sessions_empty()}</p>
            )}
          </section>
        </div>
      </main>
    </>
  )
}
